import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  dominantDirection,
  findActiveIndex,
  formatTimecode,
  type Speaker,
  type Utterance,
} from "@rec/shared";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { InlineRename } from "./InlineRename";

/**
 * The transcript panel.
 *
 * Two performance rules hold this together, and both matter at real transcript
 * sizes (a three-hour meeting is a few thousand utterances):
 *
 *   1. The timing array lives in a ref, not in state. Lookup runs on every
 *      `timeUpdate` — several times a second — and pushing that through React's
 *      render cycle burns work to produce the same DOM.
 *   2. Rows are virtualized. Rendering four thousand paragraphs makes scrolling
 *      stutter on anything but a desktop.
 */

type TranscriptProps = {
  utterances: Utterance[];
  currentMs: number;
  onSeek: (ms: number) => void;
  /** Utterance ids to mark, e.g. from a chat citation or a search hit. */
  markedIds?: Set<string>;
  /** Every speaker in the recording, for the re-assignment menu. */
  speakers?: Speaker[];
  onRenameSpeaker?: (clusterId: string, label: string) => void;
  onReassign?: (
    utteranceId: string,
    target: { clusterId: string } | { newSpeakerLabel: string },
  ) => void;
};

/**
 * How the transcript is laid out.
 *
 * Utterances are always stored finely — one per sentence or so — because that
 * is what makes click-to-seek land on the line you clicked and what gives the
 * AI citations somewhere precise to point. This only changes how they are
 * *drawn*: `grouped` merges a speaker's consecutive lines into one paragraph so
 * a long turn reads as prose rather than a list.
 *
 * Grouping stays precise. Each line inside a merged paragraph is still its own
 * clickable span, so reading it as prose costs nothing in accuracy.
 */
export type TranscriptLayout = "split" | "grouped";

/**
 * Whether two lines belong to the same voice.
 *
 * By cluster where there is one, falling back to the label for utterances the
 * provider never attributed — those have no cluster and would otherwise each
 * become their own group.
 */
function sameSpeaker(a: Utterance, b: Utterance): boolean {
  if (a.clusterId && b.clusterId) return a.clusterId === b.clusterId;
  if (a.clusterId || b.clusterId) return false;
  return a.speakerLabel === b.speakerLabel;
}

const LAYOUT_KEY = "transcript-layout";

/** Remembered per person, per device — it is a reading preference, not tenant data. */
export function useTranscriptLayout(): [TranscriptLayout, (l: TranscriptLayout) => void] {
  const [layout, setLayout] = useState<TranscriptLayout>(() => {
    const stored = localStorage.getItem(LAYOUT_KEY);
    return stored === "grouped" ? "grouped" : "split";
  });

  return [
    layout,
    (next) => {
      localStorage.setItem(LAYOUT_KEY, next);
      setLayout(next);
    },
  ];
}

/**
 * "Who said this?" for one line.
 *
 * Portalled and fixed-positioned rather than nested in the row, because the
 * transcript is a virtualized scroll container: a menu rendered inside a row is
 * clipped by the container's `overflow-y-auto`, and the rows themselves are
 * absolutely positioned and recycled underneath it.
 *
 * It closes on scroll for the same reason — the anchor moves and re-measuring
 * on every frame is a lot of work to keep a menu glued to a row somebody has
 * already scrolled past.
 */
function SpeakerMenu({
  anchor,
  speakers,
  currentClusterId,
  onPick,
  onClose,
}: {
  anchor: DOMRect;
  speakers: Speaker[];
  currentClusterId: string | null;
  onPick: (target: { clusterId: string } | { newSpeakerLabel: string }) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation("recording");
  const [newName, setNewName] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const onPointer = (e: PointerEvent) => {
      const target = e.target as HTMLElement;
      if (ref.current?.contains(target)) return;
      // Let the trigger handle its own click, or re-opening would fight this
      // close and the button would never toggle shut.
      if (target.closest("[data-speaker-trigger]")) return;
      onClose();
    };

    document.addEventListener("keydown", onKey);
    // `true` so this fires before the row's own click handler seeks the player.
    document.addEventListener("pointerdown", onPointer, true);
    /**
     * Scroll closes the menu (it is fixed-positioned and would drift) — but
     * not in its opening instant. On touch, the momentum of the scroll that
     * brought the line on screen is still running when the menu opens, and
     * those events closed it before a finger could reach it.
     */
    const openedAt = Date.now();
    const onScroll = () => {
      if (Date.now() - openedAt > 350) onClose();
    };
    window.addEventListener("scroll", onScroll, true);

    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer, true);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [onClose]);

  // Flip above the anchor when there is not enough room below, so the menu is
  // never half off-screen on a line near the bottom of the panel.
  const MENU_HEIGHT = 280;
  const below = anchor.bottom + 6;
  const flip = below + MENU_HEIGHT > window.innerHeight;

  return createPortal(
    <div
      ref={ref}
      role="dialog"
      aria-label={t("transcript.menuLabel")}
      className={cn(
        "fixed z-50 w-60 rounded-lg border border-border bg-surface p-1",
        "shadow-[var(--shadow-md)]",
      )}
      style={{
        top: flip ? Math.max(8, anchor.top - MENU_HEIGHT - 6) : below,
        left: Math.min(anchor.left, window.innerWidth - 248),
      }}
    >
      <p className="px-2 py-1.5 text-[11px] font-medium tracking-wide text-text-faint uppercase">
        {t("transcript.menuHeading")}
      </p>

      <div className="max-h-44 overflow-y-auto">
        {speakers.map((speaker) => (
          <button
            key={speaker.id}
            type="button"
            onClick={() => onPick({ clusterId: speaker.id })}
            className={cn(
              "flex w-full items-center justify-between gap-2 rounded px-2 py-1.5",
              "text-left text-[13px] transition-colors hover:bg-surface-hover",
              speaker.id === currentClusterId ? "text-text" : "text-text-muted",
            )}
          >
            <span dir="auto" className="truncate">{speaker.label}</span>
            {speaker.id === currentClusterId ? (
              <span className="shrink-0 text-[11px] text-text-faint">{t("transcript.current")}</span>
            ) : null}
          </button>
        ))}
      </div>

      {/*
        Diarization merging two people into one voice is the common failure, and
        no existing speaker is the right answer for it — so there has to be a
        way to name somebody who is not in the list yet.
      */}
      <form
        className="mt-1 border-t border-border pt-1"
        onSubmit={(e) => {
          e.preventDefault();
          const label = newName.trim();
          if (label) onPick({ newSpeakerLabel: label });
        }}
      >
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          dir="auto"
          placeholder={t("transcript.someoneElse")}
          aria-label={t("transcript.newSpeakerLabel")}
          className={cn(
            "w-full rounded bg-transparent px-2 py-1.5 text-[13px]",
            "placeholder:text-text-faint focus:bg-surface-hover focus:outline-none",
          )}
        />
      </form>
    </div>,
    document.body,
  );
}

export function Transcript({
  utterances,
  currentMs,
  onSeek,
  markedIds,
  speakers,
  onRenameSpeaker,
  onReassign,
  layout = "split",
}: TranscriptProps & { layout?: TranscriptLayout }) {
  const { t } = useTranslation("recording");
  const scrollRef = useRef<HTMLDivElement>(null);

  /** Which line's "who said this" menu is open, and where to draw it. */
  const [menu, setMenu] = useState<{
    utteranceId: string;
    clusterId: string | null;
    rect: DOMRect;
  } | null>(null);

  const canEdit = Boolean(onReassign && speakers && speakers.length > 0);
  const closeMenu = useCallback(() => setMenu(null), []);

  // Sorted start times, rebuilt only when the transcript itself changes.
  const startTimes = useMemo(() => utterances.map((u) => u.startMs), [utterances]);

  const activeUtterance = findActiveIndex(startTimes, currentMs);

  /**
   * The rows actually drawn.
   *
   * In `split` each row is one utterance. In `grouped`, consecutive utterances
   * by the same speaker become one row holding several — merged for reading,
   * not merged in the data.
   */
  const rows = useMemo(() => {
    if (layout === "split") return utterances.map((u) => [u]);

    const grouped: Utterance[][] = [];
    for (const utterance of utterances) {
      const last = grouped[grouped.length - 1];
      // Grouped by cluster, not by label: two speakers can share a name, and
      // merging them visually would hide the very mis-attribution somebody
      // opened this panel to fix.
      if (last && sameSpeaker(last[0]!, utterance)) {
        last.push(utterance);
      } else {
        grouped.push([utterance]);
      }
    }
    return grouped;
  }, [utterances, layout]);

  /** Which row holds the utterance currently playing. */
  const activeIndex = useMemo(() => {
    if (activeUtterance < 0) return -1;
    if (layout === "split") return activeUtterance;

    const active = utterances[activeUtterance];
    if (!active) return -1;
    return rows.findIndex((row) => row.some((u) => u.id === active.id));
  }, [activeUtterance, layout, rows, utterances]);

  /**
   * Stable identity matters here: `getItemKey` participates in the
   * virtualizer's measurement memo, and an inline arrow would force a full
   * O(rows) rebuild of the measurements array on every render — and this
   * component renders several times a second while playback advances.
   */
  const getItemKey = useCallback(
    (index: number) => rows[index]?.[0]?.id ?? index,
    [rows],
  );

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    // The virtualizer measures the real height after mount, so this only has to
    // be close — but "close" matters on the first paint, before anything is
    // measured. Two lines of dense script is nearer 100px than the 76 this used
    // to guess, and guessing low is what puts rows on top of each other for the
    // frame before measurement lands.
    estimateSize: () => 100,
    overscan: 12,
    getItemKey,
  });

  /*
   * There is deliberately no container-width watcher calling
   * `virtualizer.measure()` here. One existed, and it *caused* stale heights
   * rather than curing them: `measure()` only clears the size cache — nothing
   * in the library re-measures a row whose box did not change afterwards, so
   * every row the rewrap left at the same height fell back to the estimate
   * permanently. The per-row ResizeObserver already reports every row a width
   * change actually rewrapped, which is the whole job.
   */

  // Follow playback — but stop following the moment the reader scrolls away,
  // and resume when they scroll back to the active line. Yanking someone's
  // scroll position while they are reading is the fastest way to make a
  // transcript panel useless.
  const [following, setFollowing] = useState(true);
  const lastScrolledIndex = useRef(-1);

  useEffect(() => {
    if (!following || activeIndex < 0) return;
    if (activeIndex === lastScrolledIndex.current) return;

    /**
     * Never smooth-scroll a dynamically measured list.
     *
     * While a smooth scroll is in flight the virtualizer deliberately refuses
     * to measure rows outside a buffer around the scroll target — it will not
     * let a row resize under an animation it is driving. The refusal drops the
     * measurement rather than deferring it, and nothing ever asks again: React
     * does not re-run a ref callback for a row it kept, and the ResizeObserver
     * only speaks when a box actually changes. Those rows keep `estimateSize`
     * forever and are drawn at offsets that do not match their real heights —
     * rows on top of each other.
     *
     * It is cumulative, which is why it only appeared after following a long
     * recording for a while: playback queued a new animation every few seconds,
     * so the list was almost never *not* mid-scroll, and each pass stranded the
     * rows at the edges of the overscan window — the very rows about to be read.
     * A fresh load of the same section is clean because nothing was animating
     * while it measured.
     *
     * An instant re-center on each line change moves one row height, and every
     * row it mounts is measured.
     */
    lastScrolledIndex.current = activeIndex;
    virtualizer.scrollToIndex(activeIndex, { align: "center" });
  }, [activeIndex, following, virtualizer]);

  /*
   * There is deliberately no `virtualizer.measure()` when the text changes.
   *
   * It looks like it ought to be needed — row keys are utterance ids, and ids
   * survive a language switch, so a translation swaps each line's text under a
   * key whose cached height was measured for the old text. But that staleness
   * lasts one frame and fixes itself: every row is observed by the
   * virtualizer's ResizeObserver, and a row whose text changed height gets an
   * entry with the real box; a row whose height did not change had a correct
   * cached height all along.
   *
   * `measure()` would make it worse, not better. It only *clears* the size
   * cache — it re-measures nothing. A row that survives the change keeps its
   * DOM node, React does not re-run a ref callback for an element it kept, and
   * the ResizeObserver stays silent because the box did not move. So every
   * surviving row falls back to `estimateSize()` and is drawn 100px tall
   * whatever it really is: gaps under the short ones, overlap under the long
   * ones, and it persists until the row is scrolled out and back. That is the
   * symptom this file exists to avoid, reintroduced by the cure.
   */

  const onUserScroll = useCallback(() => {
    const element = scrollRef.current;
    if (!element || activeIndex < 0) return;

    const items = virtualizer.getVirtualItems();
    const activeIsVisible = items.some((item) => item.index === activeIndex);
    setFollowing(activeIsVisible);
  }, [activeIndex, virtualizer]);

  if (utterances.length === 0) {
    return (
      <div className="flex h-full items-center justify-center p-8 text-center">
        <p className="text-sm text-text-muted">
          {t("transcript.empty")}
        </p>
      </div>
    );
  }

  return (
    <div className="relative flex h-full flex-col">
      <div
        ref={scrollRef}
        onScroll={onUserScroll}
        className="flex-1 overflow-y-auto overscroll-contain px-1"
      >
        <div
          className="relative w-full"
          style={{ height: `${virtualizer.getTotalSize()}px` }}
        >
          {virtualizer.getVirtualItems().map((row) => {
            const group = rows[row.index];
            if (!group) return null;

            const isActive = row.index === activeIndex;
            const previous = rows[row.index - 1];
            // Only repeat the speaker's name when it changes — a wall of
            // "Amin:" on every line is noise.
            const showSpeaker =
              !previous || !sameSpeaker(previous[0]!, group[0]!);
            const clusterId = group[0]!.clusterId;

            return (
              <div
                key={group[0]!.id}
                data-index={row.index}
                ref={virtualizer.measureElement}
                className="absolute top-0 left-0 w-full"
                style={{ transform: `translateY(${row.start}px)` }}
              >
                <div
                  className={cn(
                    "group relative rounded-lg px-3 py-2",
                    "transition-colors duration-150",
                    isActive && "bg-highlight",
                  )}
                >
                  {/* Active marker as a bar rather than a border, so the text
                      never shifts sideways as playback moves. */}
                  <span
                    className={cn(
                      "absolute top-2 bottom-2 left-0 w-[3px] rounded-full transition-opacity",
                      isActive ? "opacity-100" : "opacity-0",
                    )}
                    style={{ background: "var(--highlight-bar)" }}
                    aria-hidden="true"
                  />

                  {showSpeaker ? (
                    <div className="mb-1 flex items-baseline gap-2">
                      <InlineRename
                        value={group[0]!.speakerLabel}
                        label={t("transcript.renameSpeaker")}
                        className="text-[13px] font-semibold text-text"
                        inputClassName="w-40"
                        onRename={
                          onRenameSpeaker && clusterId
                            ? (label) => onRenameSpeaker(clusterId, label)
                            : undefined
                        }
                      />
                      <span className="font-mono text-[11px] tabular-nums text-text-faint">
                        {formatTimecode(group[0]!.startMs)}
                      </span>
                    </div>
                  ) : null}

                  {/*
                    Every utterance stays its own button even when grouped, so a
                    merged paragraph seeks to the sentence you clicked rather
                    than to wherever the speaker started talking. Grouping is a
                    reading choice; it must not cost precision.
                  */}
                  {/*
                    Two levels of `dir="auto"`, because there are two questions.

                    On the paragraph it decides which way the utterances flow —
                    an Arabic turn must read right to left. On each utterance it
                    decides that line's own base direction, so a French aside
                    inside an Arabic turn renders left to right rather than
                    being reordered into the sentence around it.

                    One without the other is not enough: the paragraph alone
                    leaves a Latin-first line scrambled, and the spans alone
                    leave the lines themselves in the wrong order.
                  */}
                  <p
                    dir={dominantDirection(group.map((u) => u.text).join(" "))}
                    className="text-[14.5px] leading-relaxed"
                  >
                    {group.map((utterance, i) => {
                      const isCurrent =
                        activeUtterance >= 0 &&
                        utterances[activeUtterance]?.id === utterance.id;
                      const isMarked = markedIds?.has(utterance.id) ?? false;

                      return (
                        <span
                          key={utterance.id}
                          dir={dominantDirection(utterance.text)}
                          className="group/line"
                        >
                          <button
                            type="button"
                            onClick={() => onSeek(utterance.startMs)}
                            title={formatTimecode(utterance.startMs)}
                            aria-current={isCurrent ? "true" : undefined}
                            className={cn(
                              "cursor-pointer rounded text-start transition-colors",
                              isCurrent ? "text-text" : "text-text-muted",
                              "hover:text-text hover:underline hover:decoration-border",
                              isMarked && !isCurrent && "bg-accent-subtle/50",
                            )}
                          >
                            {utterance.text}
                          </button>

                          {/*
                            Re-attribution is per line, so the control has to be
                            per line too — the speaker heading covers a whole
                            run and this exists for when one line inside that
                            run belongs to somebody else.
                          */}
                          {canEdit ? (
                            <button
                              type="button"
                              data-speaker-trigger
                              aria-label={t("transcript.changeSpeakerLabel", { text: utterance.text.slice(0, 40) })}
                              title={t("transcript.changeSpeakerTitle")}
                              onClick={(e) =>
                                setMenu({
                                  utteranceId: utterance.id,
                                  clusterId: utterance.clusterId,
                                  rect: e.currentTarget.getBoundingClientRect(),
                                })
                              }
                              className={cn(
                                "touch-show ml-1 inline-flex translate-y-[1px] cursor-pointer rounded p-0.5 align-baseline",
                                "text-text-faint opacity-0 transition-opacity",
                                "group-hover/line:opacity-100 hover:bg-surface-hover hover:text-text",
                                "focus-visible:opacity-100",
                                menu?.utteranceId === utterance.id && "opacity-100",
                              )}
                            >
                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 16 16"
                                fill="none"
                                aria-hidden="true"
                              >
                                <path
                                  d="M8 8a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Zm-5 5a5 5 0 0 1 10 0"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                />
                              </svg>
                            </button>
                          ) : null}
                          {i < group.length - 1 ? " " : ""}
                        </span>
                      );
                    })}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Offer the way back rather than stealing scroll. */}
      {!following && activeIndex >= 0 ? (
        <button
          type="button"
          onClick={() => {
            setFollowing(true);
            lastScrolledIndex.current = -1;
          }}
          className={cn(
            "absolute bottom-4 left-1/2 -translate-x-1/2",
            "rounded-full border border-border bg-surface px-3.5 py-1.5",
            "text-[13px] font-medium text-text shadow-[var(--shadow-md)]",
            "transition-colors hover:bg-surface-hover",
          )}
        >
          {t("player.jumpToCurrent")}
        </button>
      ) : null}

      {menu && speakers && onReassign ? (
        <SpeakerMenu
          anchor={menu.rect}
          speakers={speakers}
          currentClusterId={menu.clusterId}
          onClose={closeMenu}
          onPick={(target) => {
            onReassign(menu.utteranceId, target);
            closeMenu();
          }}
        />
      ) : null}
    </div>
  );
}
